import React, { Component } from 'react';
import PropTypes from 'prop-types';
import pick from 'lodash/pick';
import throttle from 'lodash/throttle';
import isEqual from 'lodash/isEqual';
import set from 'lodash/set';
import AdvForm from '../../CommonComponents/AdvForm';
import Footer from '../Footer/FooterContainer';
import PlanImpact from '../PlanImpact/PlanImpactContainer';
import Loading from '../Loading/LoadingView';
import {
  isNumeric,
  isNumericFormatted,
  isUuid,
  createCustomPropType,
} from '../../../js/app/react/validators';
import ValidationErrors from '../ValidationErrors/ValidationErrorsContainer';
import AdvSlider from '../../CommonComponents/AdvSlider';
import AdvInput, { advInputTextChanged } from '../../CommonComponents/AdvInput';
import AdvInputWrapper from '../../CommonComponents/AdvInputWrapper';
import { inputCheckboxClicked } from '../../Profile/Accounts_react/AccountsUtils';
import CompareGoals from '../CompareGoals/CompareGoalsContainer';
import { ADDITIONAL_SAVINGS_LABEL_DICTIONARY } from '../WhatIfsConstants';

class AdditionalSavingsView extends Component {
  constructor() {
    super();
    this.throttledFetchPlanImpact = throttle(this.throttledFetchPlanImpact.bind(this), 1000);
    this.remainingCashflow = this.remainingCashflow.bind(this);
    this.formattedRemainingCashflow = this.formattedRemainingCashflow.bind(this);
    this.cashflowClassName = this.cashflowClassName.bind(this);
    this.cashflowLabel = this.cashflowLabel.bind(this);
    this.onAdditionalSavingsBlur = this.onAdditionalSavingsBlur.bind(this);
    this.state = {
      sliderValue: null,
    };
  }

  UNSAFE_componentWillMount() {
    this.setState({
      sliderValue: this.props.allocatedCashflow,
    });
  }

  componentDidMount() {
    const {
      fetchData,
      clientId,
      fpId,
      goalId,
      fetchInitialPlanImpact,
      whatIf,
      whatIfTemplate,
    } = this.props;
    fetchData(clientId, fpId, goalId, whatIfTemplate.WhatIfType);
    fetchInitialPlanImpact(clientId, fpId, goalId, whatIf, whatIfTemplate);
  }

  UNSAFE_componentWillReceiveProps(next) {
    if (this.state.sliderValue !== next.allocatedCashflow) {
      this.setState({
        sliderValue: next.allocatedCashflow,
      });
    }
  }

  componentDidUpdate(previousProps) {
    if (previousProps === this.props) {
      return;
    }

    const impactTriggerProps = ['fpId', 'isOPLMonteCarlo'];

    if (!isEqual(pick(this.props, impactTriggerProps), pick(previousProps, impactTriggerProps))) {
      this.throttledFetchPlanImpact();
    }
  }

  componentWillUnmount() {
    const { clearCompareGoalsData, clientId } = this.props;
    clearCompareGoalsData(clientId);
  }

  onAdditionalSavingsBlur(valueArg) {
    const { goalId, clientId, whatIfTemplate, storeData, maxCashflow, fpId, whatIf } = this.props;

    let value = valueArg;
    if (isNumericFormatted(valueArg)) {
      if (typeof valueArg === 'string') {
        value = +valueArg.replace(/[,]/g, '');
      }
    } else {
      value = 0;
    }
    if (value > maxCashflow) {
      value = Math.max(maxCashflow, 0);
    }
    advInputTextChanged(
      clientId,
      storeData(goalId, fpId, whatIf, whatIfTemplate, whatIfTemplate.WhatIfType),
      'WhatIf.AllocatedCashflow',
    )(value);
  }

  remainingCashflow(value) {
    const { availableCashflow, totalAllocatedCashflowOtherGoals } = this.props;

    return Math.trunc(availableCashflow - value - totalAllocatedCashflowOtherGoals);
  }

  cashflowClassName(value) {
    if (this.remainingCashflow(value) < 0) {
      return 'what-if-remaining-cashflow negative';
    }
    return 'what-if-remaining-cashflow';
  }

  cashflowLabel(value) {
    if (this.remainingCashflow(value) < 0) {
      return 'shortage';
    }
    return 'remaining';
  }

  formattedRemainingCashflow(value) {
    const { ADV } = window;
    const valueToFormat = Math.abs(this.remainingCashflow(value));
    if (valueToFormat > 99999) {
      return `${ADV.util.short_number_format(Math.abs(this.remainingCashflow(value)))}`;
    }
    return `${ADV.util.number_format(Math.abs(this.remainingCashflow(value)))}`;
  }

  throttledFetchPlanImpact() {
    const { clientId, fpId, goalId, whatIf, fetchPlanImpact, whatIfTemplate } = this.props;
    fetchPlanImpact(clientId, fpId, goalId, whatIf, whatIfTemplate);
  }

  render() {
    const {
      goalId,
      clientId,
      storeData,
      allocatedCashflow,
      maxCashflow,
      loading,
      planImpactItems,
      title,
      groupTitle,
      fpId,
      whatIf,
      whatIfTemplate,
      applyWhatIf,
      areOptionsLoaded,
      allocateExcessCashflow,
      compareGoalsClicked,
      compareGoalsOpen,
      goalsAdditionalSavingsWhatIfs,
      isSaveRemainingCashflowHandlingMode,
      availableCashflow,
    } = this.props;

    const label = ADDITIONAL_SAVINGS_LABEL_DICTIONARY[whatIf.WhatIfType] || 'Additional Savings';

    const { ADV } = window;
    const { sliderValue } = this.state;
    return (
      <div className="what-ifs-layout">
        {loading && <Loading />}
        <div className="what-ifs-section what-ifs-layout-header">
          <h3>What-If...</h3>
        </div>
        <div className="what-ifs-layout-content">
          <h4 className="what-if-group-title">{groupTitle}</h4>
          {areOptionsLoaded && (
            <div className="what-if-group-widget">
              <button
                type="button"
                className="what-if-compare-goals"
                onClick={compareGoalsClicked(clientId)}
              >
                Compare Goals
              </button>
              <span className={this.cashflowClassName(sliderValue)}>
                <i>$</i>
                {this.formattedRemainingCashflow(sliderValue)}
                <span className="remaining-cashflow-label">{this.cashflowLabel(sliderValue)}</span>
              </span>
            </div>
          )}
          <AdvForm>
            <input type="hidden" value={ADV.util.guid()} />
            <ValidationErrors clientId={clientId} />
            <ul className={`what-ifs-form-fields what-ifs-box${areOptionsLoaded ? ' loaded' : ''}`}>
              <li className="what-ifs-form-field">
                <h5 className="what-if-title">{title}</h5>
              </li>
              <li className="what-ifs-form-field">
                <label htmlFor="AdditionalSavingsSlider" className="additional-savings-label">
                  <span className="what-ifs-label-text">{label}</span>
                  <AdvSlider
                    id="AdditionalSavingsSlider"
                    className="additional-savings-slider"
                    min={0}
                    max={maxCashflow}
                    value={allocatedCashflow}
                    onChange={value => {
                      storeData(
                        goalId,
                        fpId,
                        whatIf,
                        whatIfTemplate,
                        whatIfTemplate.WhatIfType,
                      )(set({}, 'WhatIf.AllocatedCashflow', value), clientId);
                    }}
                  />
                </label>
              </li>
              <li className="what-ifs-form-field">
                <label>
                  <span className="what-ifs-label-text">Monthly Additional Savings Amount</span>
                  <AdvInputWrapper hasCurrency className="what-ifs-input-wrapper">
                    <AdvInput
                      autoComplete="off"
                      id="AdditionalSavingsInput"
                      type="text"
                      dataType="money"
                      maxLength="14"
                      onBlur={this.onAdditionalSavingsBlur}
                      value={ADV.util.formatValue(sliderValue, 'money')}
                      className="what-ifs-input"
                    />
                  </AdvInputWrapper>
                </label>
              </li>
            </ul>
            {isSaveRemainingCashflowHandlingMode && (
              <ul
                className={`what-ifs-form-fields what-ifs-form-fields-no-box${
                  areOptionsLoaded ? ' loaded' : ''
                }`}
              >
                <li className="what-ifs-form-field what-ifs-form-field-no-box">
                  <label
                    htmlFor="AllocateExcessCashflow"
                    className="what-ifs-age-label what-ifs-checkbox"
                  >
                    <input
                      type="checkbox"
                      id="AllocateExcessCashflow"
                      className="what-ifs-checkbox-toggle"
                      checked={allocateExcessCashflow}
                      onChange={inputCheckboxClicked(
                        clientId,
                        storeData(goalId, fpId, whatIf, whatIfTemplate, whatIfTemplate.WhatIfType),
                        'WhatIf.AllocateExcessCashflow',
                      )}
                    />
                    <i className="what-ifs-checkbox-mark" />
                    <span className="what-ifs-checkbox-label-text">
                      Allocate excess cashflow to this goal
                    </span>
                  </label>
                </li>
              </ul>
            )}
          </AdvForm>
        </div>
        <PlanImpact clientId={clientId} planImpactItems={planImpactItems} />
        <Footer
          clientId={clientId}
          applyToPlanCallback={applyWhatIf(
            clientId,
            fpId,
            goalId,
            whatIf,
            whatIfTemplate,
            goalsAdditionalSavingsWhatIfs,
          )}
        />
        {compareGoalsOpen && (
          <CompareGoals
            clientId={clientId}
            availableCashflow={availableCashflow}
            maxCashflow={maxCashflow}
          />
        )}
      </div>
    );
  }
}
AdditionalSavingsView.defaultProps = {
  title: 'Untitled What If',
  groupTitle: 'Untitled What If Group',
  maxCashflow: 10000,
  allocatedCashflow: 0,
  availableCashflow: 0,
  totalAllocatedCashflowOtherGoals: 0,
  allocateExcessCashflow: false,
};

AdditionalSavingsView.propTypes = {
  goalId: createCustomPropType(isUuid).isRequired,
  fpId: createCustomPropType(isUuid).isRequired,
  clientId: createCustomPropType(isNumeric).isRequired,
  title: PropTypes.string,
  groupTitle: PropTypes.string,
  areOptionsLoaded: PropTypes.bool.isRequired,
  whatIf: PropTypes.shape({}).isRequired,
  whatIfTemplate: PropTypes.shape({}).isRequired,
  storeData: PropTypes.func.isRequired,
  fetchData: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  fetchPlanImpact: PropTypes.func.isRequired,
  fetchInitialPlanImpact: PropTypes.func.isRequired,
  planImpactItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      current: PropTypes.string.isRequired,
      proposed: PropTypes.string.isRequired,
    }),
  ).isRequired,
  goalsAdditionalSavingsWhatIfs: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  applyWhatIf: PropTypes.func.isRequired,
  allocatedCashflow: PropTypes.number,
  maxCashflow: PropTypes.number,
  availableCashflow: PropTypes.number,
  totalAllocatedCashflowOtherGoals: PropTypes.number,
  allocateExcessCashflow: PropTypes.bool,
  compareGoalsOpen: PropTypes.bool.isRequired,
  compareGoalsClicked: PropTypes.func.isRequired,
  clearCompareGoalsData: PropTypes.func.isRequired,
  isSaveRemainingCashflowHandlingMode: PropTypes.bool.isRequired,
};

export default AdditionalSavingsView;
